import { useState, useEffect, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { useBusiness } from "../../../../Common/Hooks/useBusiness";
import { useApi } from "../../../../Common/Hooks/useApi";
import { useAuth } from "../../../../Common/Hooks/useAuth";
import { isErr } from "../../../../Common/Utils/Result";
import { eventBus } from "../../../../Common/EventBus";
import { Notification } from "../../../../Components/Notifications";
import { Button } from "../../../../Components/Button";
import { CustomerBillingDto, DetailedSubscriptionDto } from "../../../../Common/Types-DTOs/DetailedSubscription";
import { FiCheck } from "react-icons/fi";
import { MdRocketLaunch } from "react-icons/md";
import { isAdmin } from "../../../../Common/Utils/isAdmin";
import { CopilotIncludedInPlan } from "./CopilotIncludedInPlan";
import { BillingDetailsSection, PaymentMethodSection } from "./CustomerBilling";
import {
  ContentGrid,
  MainColumn,
  SideColumn,
  Card,
  CardHeader,
  PlanBadge,
  CornerPlanBadge,
  PlanName,
  PlanPrice,
  PriceAmount,
  PricePeriod,
  RenewalInfo,
  FeaturesSection,
  FeatureItem,
  FeatureIcon,
  ActionsRow,
  BillingInfoSection,
  BillingItem,
  InvoicesList,
  InvoiceItem,
  CancelButton,
  EmptyStateWrapper,
  EmptyStateTitle,
  EmptyStateDescription,
  EmptyStateActions,
  TrialBanner,
  IconWrapper,
  CancelWarning,
  SkeletonBox,
} from "./Styles";
interface ChatbotSubscriptionProps {
  onClose?: () => void;
}


/** The chatbot subscription: trial, plan, payment method, billing and invoices. */
export const ChatbotSubscription = ({ onClose }: ChatbotSubscriptionProps = {}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { business, getDetailedSubscription, getCancelSubscriptionUrl, getCustomerPortalUrl } = useBusiness();
  const { getChatCreditsEndpoint } = useApi();
  const { clientUser } = useAuth();
  const [detailedSubscription, setDetailedSubscription] = useState<DetailedSubscriptionDto | null>(null);
  const [customerBilling, setCustomerBilling] = useState<CustomerBillingDto | null>(null);
  const [trialInfo, setTrialInfo] = useState<{ endDate: Date; isActive: boolean } | null>(null);
  const [chatCredits, setChatCredits] = useState<{
    unlimited: boolean;
    availableChatCredits: number;
  } | null>(null);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const userIsAdmin = isAdmin(business, clientUser);

  useEffect(() => {
    if (!business) {
      return;
    }

    // Always fetch - the backend will return either subscription details or trial info
    const fetchDetailedSubscription = async () => {
      setIsLoading(true);
      const result = await getDetailedSubscription();
      if (!isErr(result)) {
        // Backend returns { subscription: DetailedSubscriptionDto | null, trial: TrialInfo | null }
        setDetailedSubscription(result.value.subscription);
        setTrialInfo(result.value.trial);
        setCustomerBilling(result.value.billing);
      } else {
        console.error("Error fetching subscription:", result.error);
      }
      setIsLoading(false);
    };

    fetchDetailedSubscription();

    const fetchChatCredits = async () => {
      const result = await getChatCreditsEndpoint();
      if (!isErr(result) && result.value) {
        setChatCredits({
          unlimited: result.value.unlimited,
          availableChatCredits: result.value.availableChatCredits,
        });
      }
    };

    fetchChatCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id]);

  const handleCancelSubscription = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    if (loadingCancel) return;

    setLoadingCancel(true);
    try {
      const result = await getCancelSubscriptionUrl();

      if (isErr(result)) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification("error", t("pricing.subscriptionContent.notifications.cancelUrlFailed"))
        );
        return;
      }

      if (!result.value || typeof result.value !== "string" || result.value.trim() === "") {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification("error", t("pricing.subscriptionContent.notifications.cancelUrlInvalid"))
        );
        return;
      }

      window.open(result.value, "_blank");
    } catch (error) {
      console.error("Error canceling subscription:", error);
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("pricing.subscriptionContent.notifications.cancelUnexpectedError"))
      );
    } finally {
      setLoadingCancel(false);
    }
  };

  const handleManagePayment = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();

    try {
      const result = await getCustomerPortalUrl();

      if (isErr(result)) {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification("error", t("pricing.subscriptionContent.notifications.portalUrlFailed"))
        );
        return;
      }

      if (!result.value || typeof result.value !== "string" || result.value.trim() === "") {
        eventBus.emit(
          "NOTIFICATION",
          Notification.buildNotification("error", t("pricing.subscriptionContent.notifications.portalUrlInvalid"))
        );
        return;
      }

      window.open(result.value, "_blank");
    } catch (error) {
      console.error("Error opening management portal:", error);
      eventBus.emit(
        "NOTIFICATION",
        Notification.buildNotification("error", t("pricing.subscriptionContent.notifications.portalUnexpectedError"))
      );
    }
  };

  if (!business) {
    return null;
  }

  // Check if user is admin before showing subscription details
  if (!userIsAdmin) {
    return (
      <>
        <EmptyStateWrapper>
          <CornerPlanBadge>{t("pricing.subscriptionContent.copilotPlan.badge")}</CornerPlanBadge>
          <EmptyStateTitle>{t("pricing.subscriptionContent.restrictedAccess.title")}</EmptyStateTitle>
          <EmptyStateDescription>
            {t("pricing.subscriptionContent.restrictedAccess.description")}
          </EmptyStateDescription>
        </EmptyStateWrapper>
      </>
    );
  }

  // Show loading skeleton while fetching data
  if (isLoading) {
    return (
      <>
        <ContentGrid>
          <MainColumn>
            {/* Plan Card Skeleton */}
            <Card>
              <CardHeader>
                <div style={{ flex: 1 }}>
                  <SkeletonBox width="6rem" height="1.5rem" $borderRadius="9999px" style={{ marginBottom: '0.5rem' }} />
                  <SkeletonBox width="12rem" height="2rem" style={{ marginBottom: '0.5rem' }} />
                  <SkeletonBox width="16rem" height="1rem" />
                </div>
                <div>
                  <SkeletonBox width="6rem" height="2.5rem" />
                </div>
              </CardHeader>

              {/* Features Skeleton */}
              <FeaturesSection>
                {[1, 2, 3].map((i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <SkeletonBox width="1.25rem" height="1.25rem" $borderRadius="50%" />
                    <SkeletonBox width="70%" height="1rem" />
                  </div>
                ))}
              </FeaturesSection>

              {/* Action Buttons Skeleton */}
              <ActionsRow style={{ marginTop: '1rem' }}>
                <SkeletonBox width="10rem" height="2.5rem" />
                <SkeletonBox width="12rem" height="2.5rem" />
              </ActionsRow>
            </Card>

            {/* Payment Method Card Skeleton */}
            <Card>
              <CardHeader>
                <SkeletonBox width="10rem" height="1.5rem" />
              </CardHeader>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', border: '1px solid #E5E7EB', borderRadius: '0.5rem', backgroundColor: '#F9FAFB' }}>
                <SkeletonBox width="3rem" height="3rem" $borderRadius="0.5rem" />
                <div style={{ flex: 1 }}>
                  <SkeletonBox width="12rem" height="1rem" style={{ marginBottom: '0.5rem' }} />
                  <SkeletonBox width="8rem" height="0.875rem" />
                </div>
                <SkeletonBox width="4rem" height="1.5rem" />
              </div>
            </Card>
          </MainColumn>

          <SideColumn>
            {/* Billing Info Card Skeleton */}
            <Card>
              <CardHeader>
                <SkeletonBox width="12rem" height="1.5rem" />
              </CardHeader>
              <BillingInfoSection>
                {[1, 2, 3, 4].map((i) => (
                  <BillingItem key={i}>
                    <SkeletonBox width="6rem" height="0.75rem" style={{ marginBottom: '0.25rem' }} />
                    <SkeletonBox width="10rem" height="1rem" />
                  </BillingItem>
                ))}
                <SkeletonBox width="8rem" height="2rem" style={{ marginTop: '0.5rem' }} />
              </BillingInfoSection>
            </Card>

            {/* Invoices Card Skeleton */}
            <Card>
              <CardHeader style={{ marginBottom: '1rem' }}>
                <SkeletonBox width="6rem" height="1.5rem" />
                <SkeletonBox width="4rem" height="1rem" />
              </CardHeader>
              <InvoicesList>
                {[1, 2, 3].map((i) => (
                  <InvoiceItem key={i}>
                    <div>
                      <SkeletonBox width="8rem" height="1rem" style={{ marginBottom: '0.25rem' }} />
                      <SkeletonBox width="10rem" height="0.875rem" />
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <SkeletonBox width="4rem" height="1rem" />
                      <SkeletonBox width="1.5rem" height="1.5rem" $borderRadius="50%" />
                    </div>
                  </InvoiceItem>
                ))}
              </InvoicesList>
            </Card>
          </SideColumn>
        </ContentGrid>
      </>
    );
  }

  const withBilling = (main: ReactNode) =>
    customerBilling ? (
      <ContentGrid>
        <MainColumn>
          {main}
          <PaymentMethodSection billing={customerBilling} onManage={handleManagePayment} />
        </MainColumn>
        <SideColumn>
          <BillingDetailsSection billing={customerBilling} onManage={handleManagePayment} />
        </SideColumn>
      </ContentGrid>
    ) : (
      main
    );

  // A DD plan that includes the Copilot: nothing to contract, unless it's also paid on its own
  const dueDiligencePlan = business.credits?.dueDiligencePlan;
  if (!detailedSubscription && dueDiligencePlan?.includesChat) {
    return withBilling(<CopilotIncludedInPlan planLookupKey={dueDiligencePlan.lookupKey} />);
  }

  // If we have trial info from backend, use it; otherwise fall back to business data
  const isOnTrial = trialInfo?.isActive || (business.trialEndDate && new Date(business.trialEndDate) > new Date());

  // Show trial info when user is on trial without subscription
  if (trialInfo && trialInfo.isActive && !detailedSubscription) {
    const daysRemaining = Math.ceil((trialInfo.endDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));

    return withBilling(
      <>
        <EmptyStateWrapper>
          <CornerPlanBadge>{t("pricing.subscriptionContent.copilotPlan.badge")}</CornerPlanBadge>
          <EmptyStateTitle>{t("pricing.subscriptionContent.trial.activeTitle")}</EmptyStateTitle>
          <EmptyStateDescription>
            {t("pricing.subscriptionContent.trial.activeDescriptionPrefix")}{" "}
            <strong>{trialInfo.endDate.toLocaleDateString("es-ES", {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}</strong>
          </EmptyStateDescription>

          <TrialBanner $variant="info" style={{ fontSize: '1.1rem', padding: '1rem 1.5rem' }}>
            ⏱️ <Trans
              i18nKey="pricing.subscriptionContent.trial.daysRemaining"
              count={daysRemaining}
              values={{ count: daysRemaining }}
              components={{ 1: <strong /> }}
            />
          </TrialBanner>

          <EmptyStateDescription style={{ marginTop: '1.5rem', fontSize: '0.95rem' }}>
            {t("pricing.subscriptionContent.trial.continueDescription")}
          </EmptyStateDescription>

          <EmptyStateActions>
            <Button onClick={() => {
              onClose?.();
              navigate('/choose-plan');
            }}>
              {t("pricing.subscriptionContent.trial.subscribeButton")}
            </Button>
          </EmptyStateActions>
        </EmptyStateWrapper>
      </>
    );
  }

  // Show expired trial message
  if (trialInfo && !trialInfo.isActive && !detailedSubscription) {
    return withBilling(
      <>
        <EmptyStateWrapper>
          <CornerPlanBadge>{t("pricing.subscriptionContent.copilotPlan.badge")}</CornerPlanBadge>
          <EmptyStateTitle>{t("pricing.subscriptionContent.trial.expiredTitle")}</EmptyStateTitle>
          <EmptyStateDescription>
            {t("pricing.subscriptionContent.trial.expiredDescription")}
          </EmptyStateDescription>

          <TrialBanner $variant="warning">
            {t("pricing.subscriptionContent.trial.expiredBanner", { date: trialInfo.endDate.toLocaleDateString("es-ES") })}
          </TrialBanner>

          <EmptyStateActions>
            <Button onClick={() => {
              onClose?.();
              navigate('/choose-plan');
            }}>
              {t("pricing.subscriptionContent.trial.subscribeButton")}
            </Button>
          </EmptyStateActions>
        </EmptyStateWrapper>
      </>
    );
  }

  // Show no subscription message (neither trial nor subscription)
  if (!detailedSubscription) {
    return withBilling(
      <>
        <EmptyStateWrapper>
          <CornerPlanBadge>{t("pricing.subscriptionContent.copilotPlan.badge")}</CornerPlanBadge>
          <EmptyStateTitle>{t("pricing.subscriptionContent.noPlan.title")}</EmptyStateTitle>
          <EmptyStateDescription>
            {t("pricing.subscriptionContent.noPlan.description")}
          </EmptyStateDescription>

          {chatCredits && !chatCredits.unlimited && (
            <TrialBanner $variant="info" style={{ fontSize: "1.1rem", padding: "1rem 1.5rem" }}>
              💬 <Trans
                i18nKey="pricing.subscriptionContent.noPlan.creditsRemaining"
                count={chatCredits.availableChatCredits}
                values={{ count: chatCredits.availableChatCredits }}
                components={{ 1: <strong /> }}
              />
            </TrialBanner>
          )}

          <EmptyStateActions>
            <Button
              onClick={() => {
                onClose?.();
                navigate('/choose-plan');
              }}
              style={{ flex: 1, minWidth: 0, justifyContent: "center" }}
            >
              {t("pricing.subscriptionContent.noPlan.subscribeButton")}
            </Button>
            {chatCredits && !chatCredits.unlimited && (
              <Button
                onClick={() => {
                  onClose?.();
                  navigate("/buy-credits");
                }}
                style={{
                  flex: 1,
                  minWidth: 0,
                  justifyContent: "center",
                  backgroundColor: "#ffffff",
                  color: "#001d30",
                  border: "1px solid #001d30",
                }}
              >
                {t("pricing.subscriptionContent.noPlan.buyCreditsButton")}
              </Button>
            )}
          </EmptyStateActions>
        </EmptyStateWrapper>
      </>
    );
  }

  // If no subscription and no trial, show error state
  if (!detailedSubscription && !trialInfo) {
    return (
      <>
        <EmptyStateWrapper>
          <CornerPlanBadge>{t("pricing.subscriptionContent.copilotPlan.badge")}</CornerPlanBadge>
          <EmptyStateTitle>{t("pricing.subscriptionContent.errorState.loadFailedTitle")}</EmptyStateTitle>
          <EmptyStateDescription>
            {t("pricing.subscriptionContent.errorState.loadFailedDescription")}
          </EmptyStateDescription>
          <EmptyStateActions>
            <Button onClick={() => window.location.reload()}>
              {t("pricing.subscriptionContent.errorState.reloadButton")}
            </Button>
          </EmptyStateActions>
        </EmptyStateWrapper>
      </>
    );
  }

  // Additional validation for subscription data
  if (detailedSubscription && (!detailedSubscription.plan || !detailedSubscription.subscription)) {
    return (
      <>
        <EmptyStateWrapper>
          <CornerPlanBadge>{t("pricing.subscriptionContent.copilotPlan.badge")}</CornerPlanBadge>
          <EmptyStateTitle>{t("pricing.subscriptionContent.errorState.incompleteDataTitle")}</EmptyStateTitle>
          <EmptyStateDescription>
            {t("pricing.subscriptionContent.errorState.incompleteDataDescription")}
          </EmptyStateDescription>
          <EmptyStateActions>
            <Button onClick={() => window.location.reload()}>
              {t("pricing.subscriptionContent.errorState.reloadButton")}
            </Button>
          </EmptyStateActions>
        </EmptyStateWrapper>
      </>
    );
  }

  const formatCurrency = (amount: number, currency: string, isInCents: boolean = false) => {
    const amountToFormat = isInCents ? amount / 100 : amount;
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amountToFormat);
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };


  // This point is only reached if we have a valid subscription
  if (!detailedSubscription) {
    return null;
  }

  return (
    <>
      <ContentGrid>
        <MainColumn>
          <Card>
            <IconWrapper style={{ position: "absolute", top: "1.5rem", right: "1.5rem", opacity: 0.1, fontSize: "5rem", color: "#3b82f6" }}>
              <MdRocketLaunch />
            </IconWrapper>

            <CardHeader style={{ position: "relative", zIndex: 1 }}>
              <div>
                <PlanBadge>{t("pricing.subscriptionContent.copilotPlan.badge")}</PlanBadge>
                <PlanName>{detailedSubscription.plan.name}</PlanName>
                <RenewalInfo>
                  {t("pricing.subscriptionContent.plan.renewalInfo", {
                    interval: detailedSubscription.plan.interval === "month"
                      ? t("pricing.subscriptionContent.plan.billingMonthly")
                      : t("pricing.subscriptionContent.plan.billingYearly"),
                    date: formatDate(detailedSubscription.subscription.currentPeriodEnd),
                  })}
                </RenewalInfo>
              </div>
              <PlanPrice>
                <PriceAmount>{formatCurrency(detailedSubscription.plan.amount, detailedSubscription.plan.currency, false)}</PriceAmount>
                <PricePeriod>/{detailedSubscription.plan.interval === "month" ? t("pricing.subscriptionContent.plan.periodMonth") : t("pricing.subscriptionContent.plan.periodYear")}</PricePeriod>
              </PlanPrice>
            </CardHeader>

            <FeaturesSection>
              {detailedSubscription.plan.features.map((feature, index) => (
                <FeatureItem key={index}>
                  <FeatureIcon>
                    <FiCheck />
                  </FeatureIcon>
                  <span>{feature}</span>
                </FeatureItem>
              ))}
            </FeaturesSection>

            {detailedSubscription.subscription.cancelAt && (
              <CancelWarning>
                {t("pricing.subscriptionContent.plan.cancelWarning", { date: formatDate(detailedSubscription.subscription.cancelAt) })}
              </CancelWarning>
            )}

            <ActionsRow>
              {!detailedSubscription.subscription.cancelAt && !isOnTrial && (
                <CancelButton onClick={handleCancelSubscription} disabled={loadingCancel}>
                  {loadingCancel ? t("pricing.subscriptionContent.plan.cancelButtonLoading") : t("pricing.subscriptionContent.plan.cancelButton")}
                </CancelButton>
              )}
            </ActionsRow>
          </Card>

          {customerBilling && <PaymentMethodSection billing={customerBilling} onManage={handleManagePayment} />}
        </MainColumn>

        {customerBilling && (
          <SideColumn>
            <BillingDetailsSection billing={customerBilling} onManage={handleManagePayment} />
          </SideColumn>
        )}
      </ContentGrid>
    </>
  );
};
